import { type IncidentItemDto, type IncidentSeverity, type IncidentStatus } from '@api/incidents';
import { type NormalizedIncident } from '@models';

/**
 * Maps the generated `IncidentItemDto` (raw TypeORM entity graph, no column
 * aliasing) to the shared `NormalizedIncident` table row shape — the v2
 * counterpart to `normalizeIncidents`/`IncidentDto`. `incidentRule` only
 * exposes `{id, name}` on the generated type (`IncidentRuleDto`) even though
 * the backend's joined entity has more fields — dropped the `code`/`operator`/
 * `conditionValue` composed-string fallback the old hand-written
 * `normalizeIncidentsGrid` had, since the generated type doesn't carry them
 * (see `rfc-incidents-v2-table-split`'s "out of scope" section).
 */
export const normalizeIncidentsV2Grid = (incidents: IncidentItemDto[]): NormalizedIncident[] =>
  incidents.map(incident => ({
    assignedUser: '',
    building: incident.device.location?.friendlyName ?? '',
    buildingId: incident.device.location?.id ?? '',
    deviceId: incident.device.id,
    deviceName: incident.device.friendlyName ?? '',
    deviceType: incident.device.deviceModel?.deviceType2?.name ?? '',
    deviceTypeId: incident.device.deviceModel?.deviceType2?.id ?? '',
    floor: incident.device.spacePath?.[0]?.friendlyName ?? '',
    floorId: incident.device.spacePath?.[0]?.id ?? '',
    id: incident.id,
    incidentRule: incident.incidentRule?.name ?? '',
    manufacturer: incident.device.deviceModel?.maker?.name ?? '',
    model: incident.device.deviceModel?.name ?? '',
    parentId: incident.device.parentDevice?.id ?? '',
    parentName: incident.device.parentDevice?.friendlyName ?? '',
    reported: incident.createdAt ? new Date(incident.createdAt).toLocaleString('en-US') : '',
    resolved: incident.resolvedAt ? new Date(incident.resolvedAt).toLocaleString('en-US') : '',
    room: incident.device.spacePath?.[1]?.friendlyName ?? '',
    roomId: incident.device.spacePath?.[1]?.id ?? '',
    serialNumber: incident.device.additionalAttributes?.find(attr => attr.name === 'serialNumber')?.value ?? '',
    severity: incident.severity as IncidentSeverity,
    status: incident.status as IncidentStatus,
    ticketNumber: `${incident.ticketNumber}`,
  }));
